/**
 * Crown Jewel Asset Form Component
 * 
 * This component provides a modular, configurable form for adding or editing
 * crown jewel assets. It uses a section-based approach to organize fields
 * and supports easy addition of new fields via configuration.
 * 
 * Dependencies:
 * - React (useState, useEffect)
 * - FormSection, TextField, SelectField components
 * - assetFormConfig for field definitions
 * 
 * Props:
 * @param {string} clientId - ID of the selected client
 * @param {function} onSave - Callback function when form is submitted successfully
 * @param {function} onCancel - Callback function when form is cancelled
 * @param {object} initialData - Data for editing an existing asset (null for new assets)
 * @param {string} initialTab - Initial tab to be active when form is opened
 * @param {boolean} isModal - Indicates if the form is being used in a modal
 */
import React, { useState, useEffect } from 'react';
import FormSection from './FormFields/FormSection';
import TextField from './FormFields/TextField';
import SelectField from './FormFields/SelectField';
import CommentsTab from './AssetDetail/CommentsTab';
import { formSections, defaultAssetValues, formTabs } from './config/assetFormConfig';

const CrownJewelAssetForm = ({ clientId, onSave, onCancel, initialData, initialTab, fromHeatmap, isModal }) => {
  // Add component-level variable to track initialization
  const tabInitialized = React.useRef(false);
  
  console.log(`CrownJewelAssetForm rendered with initialTab: ${initialTab}, fromHeatmap: ${fromHeatmap}, isModal: ${isModal}`);

  // Initialize form data with default values or existing data
  const [formData, setFormData] = useState(defaultAssetValues);
  
  // Force activeTab to always use the passed initialTab to start
  const [activeTab, setActiveTab] = useState(initialTab || 'basic');
  
  // Add a state to track if data is fully loaded
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Add these state variables at the top of your component function
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [renderKey, setRenderKey] = useState(Date.now());
  
  // PRD v9: Add state for dynamic title key to force re-renders
  const [titleKey, setTitleKey] = useState(0);

  // PRD v9: Dynamic title function
  const getDynamicFormTitle = () => {
    const assetName = formData?.basicInfo?.name || initialData?.assetName || initialData?.basicInfo?.name;
    const isEditing = !!initialData;
    
    if (isEditing && assetName && assetName.trim() !== '') {
      return `Edit Asset: ${assetName}`;
    } else if (isEditing) {
      return 'Edit Crown Jewel Asset';
    } else if (assetName && assetName.trim() !== '') {
      return `New Asset: ${assetName}`;
    } else {
      return 'Add New Crown Jewel Asset';
    }
  };

  // PRD v9: Update modal title when asset name changes
  useEffect(() => {
    if (isModal) {
      // Update the modal title element directly
      const titleElement = document.querySelector('.asset-form-modal-title');
      if (titleElement) {
        titleElement.textContent = getDynamicFormTitle();
      }
      
      // Also trigger title key update for re-render
      setTitleKey(prev => prev + 1);
    }
  }, [formData.basicInfo?.name, isModal]);

  // Modify the effect that loads data from initialData prop
  useEffect(() => {
    console.log("Initial data changed:", initialData);
    
    // Set loading state
    setIsDataLoading(true);
    
    if (initialData) {
      try {
        // Transform flat structure to grouped structure if needed
        const transformedData = transformInitialData(initialData);
        setFormData(transformedData);
        
        // Mark data as loaded
        setDataLoaded(true);
        
        // Log the data that was loaded
        console.log("Form data set to:", transformedData);
        
        // Force a re-render after data is set
        setTimeout(() => {
          setRenderKey(Date.now());
          setIsDataLoading(false);
        }, 50);
      } catch (err) {
        console.error("Error processing form data:", err);
        setIsDataLoading(false);
      }
    } else {
      setIsDataLoading(false);
    }
  }, [initialData]);
  
  // Add a useEffect specifically for "keyControls" tab rendering
  useEffect(() => {
    // Only proceed if we're on the key controls tab
    if (activeTab === 'keyControls' && initialData) {
      console.log("On key controls tab with data - forcing re-render");
      
      // Force the key controls content to re-render
      const timer = setTimeout(() => {
        // This slight state change will trigger a re-render of the component
        setDataLoaded(prevLoaded => {
          console.log("Forcing tab content refresh");
          // Toggle and then immediately toggle back to force refresh
          return prevLoaded;
        });
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [activeTab, initialData, dataLoaded]);

  // Replace the entire useEffect that sets the tab
  useEffect(() => {
    // Force active tab to match initialTab every time initialTab changes
    console.log(`CrownJewelAssetForm: initialTab changed to ${initialTab}`);
    
    if (initialTab) {
      // Update active tab immediately
      setActiveTab(initialTab);
      
      // And again after a delay to catch race conditions
      const timer = setTimeout(() => {
        console.log(`CrownJewelAssetForm: Applying delayed tab selection: ${initialTab}`);
        setActiveTab(initialTab);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [initialTab]);
  
  // Add this effect to force re-render when tab changes
  useEffect(() => {
    // When tab changes and we have data, force a rerender
    if (initialData && activeTab === 'keyControls') {
      console.log("Key Controls tab activated with data, forcing refresh");
      setRenderKey(Date.now());
    }
  }, [activeTab, initialData]);
  
  /**
   * Transform flat data structure from legacy format to new grouped format
   * This is necessary for backward compatibility with existing data
   */
  const transformInitialData = (data) => {
    // If data is already in the new format, use it directly
    if (data.basicInfo) return data;
    
    // Otherwise transform from flat to grouped structure
    return {
      basicInfo: {
        name: data.assetName || '',
        description: data.assetDescription || '',
        location: data.assetLocation || ''
      },
      ownerInfo: {
        ownerDepartment: data.ownerDepartment || '',
        ownerName: data.ownerName || '',
        ownerEmail: data.ownerEmail || ''
      },
      securityInfo: {
        // PRD v11: Handle both new and legacy risk source fields
        riskSources: data.riskSources || 
                    (data.securityClassification === 'Internal Use' ? 'Internal' : 
                     data.securityClassification === 'External' ? 'External' : 'Internal'),
        criticalityClassification: data.criticalityClassification || '4-Medium'
      },
      keyControls: {
        ssoImplemented: data.ssoImplemented || 'No',
        twoFactorAuth: data.twoFactorAuth || 'No',
        dataEncryption: data.dataEncryption || 'No',
        backupsImplemented: data.backupsImplemented || 'No',
        networkSegmentation: data.networkSegmentation || 'No',
        userAccessReviews: data.userAccessReviews || 'No'
      },
      dataInfo: {
        // PRD v11: Handle both new and legacy data classification fields
        containsSensitiveData: data.containsSensitiveData || 
                              (data.customerPII === 'Yes' ? 'Customer PII' :
                               data.employeePII === 'Yes' ? 'Employee PII' :
                               data.sensitivePII === 'Yes' ? 'Multiple Types' : 'None')
      },
      comments: {
        generalComments: data.comments?.generalComments || data.generalComments || '',
        lastUpdated: data.comments?.lastUpdated || null,
        updatedBy: data.comments?.updatedBy || null,
        commentHistory: data.comments?.commentHistory || []
      },
      
      // Preserve the ID if it exists
      id: data.id,
      clientId: data.clientId || clientId,
      
      // Add metadata for tracking the transformation
      metadata: {
        transformedAt: new Date().toISOString(),
        version: 'v11',
        originalFormat: data.metadata?.version || 'legacy'
      }
    };
  };
  
  /**
   * Transform grouped data back to flat structure for compatibility with existing code
   */
  const transformToFlatData = () => {
    const now = new Date().toISOString();
    
    return {
      // Preserve ID and client ID
      id: initialData?.id,
      clientId: clientId,
      
      // Add or update timestamps
      createdAt: initialData?.createdAt || now,
      lastUpdated: now,
      
      // Basic information
      assetName: formData.basicInfo.name,
      assetDescription: formData.basicInfo.description,
      assetLocation: formData.basicInfo.location,
      
      // Owner information
      ownerDepartment: formData.ownerInfo.ownerDepartment,
      ownerName: formData.ownerInfo.ownerName,
      ownerEmail: formData.ownerInfo.ownerEmail,
      
      // Security information - updated for PRD v11
      riskSources: formData.securityInfo.riskSources,
      criticalityClassification: formData.securityInfo.criticalityClassification,
      
      // Key Controls
      ssoImplemented: formData.keyControls.ssoImplemented,
      twoFactorAuth: formData.keyControls.twoFactorAuth,
      dataEncryption: formData.keyControls.dataEncryption,
      backupsImplemented: formData.keyControls.backupsImplemented,
      networkSegmentation: formData.keyControls.networkSegmentation,
      userAccessReviews: formData.keyControls.userAccessReviews,
      
      // Data classification - updated for PRD v11
      containsSensitiveData: formData.dataInfo.containsSensitiveData,
      
      // Comments - PRD v23
      comments: formData.comments,
      
      // Add metadata for tracking changes
      metadata: {
        lastModified: now,
        version: 'v23',
        changes: ['riskSources', 'containsSensitiveData', 'comments']
      }
    };
  };

  /**
   * Handle changes to form fields within a specific section
   */
  const handleSectionChange = (section, e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert to flat structure for compatibility with existing code
    const flatData = transformToFlatData();
    
    // Call the onSave callback with the prepared data
    onSave(flatData);
  };

  /**
   * Render form fields from configuration for a specific section
   */
  const renderFields = (section, sectionKey) => {
    return section.fields.map((field, idx) => {
      const key = `${sectionKey}-${field.name}-${idx}`;
      
      if (field.type === 'select') {
        return (
          <SelectField
            key={key}
            label={field.label}
            name={field.name}
            value={formData[sectionKey][field.name]}
            onChange={(e) => handleSectionChange(sectionKey, e)}
            options={field.options}
            required={field.required}
            placeholder={field.placeholder}
            className="col-span-1"
          />
        );
      }
      
      return (
        <TextField
          key={key}
          type={field.type}
          label={field.label}
          name={field.name}
          value={formData[sectionKey][field.name]}
          onChange={(e) => handleSectionChange(sectionKey, e)}
          placeholder={field.placeholder}
          required={field.required}
          rows={field.rows}
          className="col-span-1"
        />
      );
    });
  };

  // Render the tab buttons with debugging info
  const renderTabButtons = () => (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
        {formTabs.map((tab) => {
          // Check if we should show a badge for this tab
          let badge = null;
          if (tab.id === 'comments' && formData?.comments?.generalComments) {
            badge = '●';
          }
          
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              {tab.label}
              {badge && (
                <span className="text-blue-500 text-xs ml-1">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );

  // Update your renderTabContent function to use the loading state
  const renderTabContent = () => {
    // Add loading indicator if data is still loading
    if (isDataLoading && initialData) {
      return (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-3"></div>
          <p className="text-gray-500">Loading asset data...</p>
        </div>
      );
    }

    // Your existing tab rendering logic, but with the key for forcing re-renders
    switch (activeTab) {
      case 'keyControls':
        return (
          <div key={`keyControls-${renderKey}`} className="animate-fadeIn">
            <h3 className="text-lg font-medium mb-4">Security Controls Implementation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Your Key Controls fields here */}
              <div className="col-span-3">
                <p className="text-sm text-gray-500 mb-4">
                  Select the security controls that have been implemented for this asset.
                </p>
              </div>
              
              <FormField
                label="Single Sign-On (SSO)"
                name="keyControls.ssoImplemented"
                type="select"
                value={formData.keyControls?.ssoImplemented || ''}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'Select...' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                  { value: 'N/A', label: 'N/A' }
                ]}
              />
              
              {/* Other controls... */}
            </div>
          </div>
        );
        
      // Other cases...
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" key={`form-${renderKey}`}>
      {/* Tab Navigation */}
      {renderTabButtons()}

      <div className="space-y-6">
        {/* Handle Comments tab separately */}
        {activeTab === 'comments' ? (
          <CommentsTab 
            asset={initialData}
            formData={formData}
            onFormDataChange={setFormData}
            onUpdate={() => {
              // Optional: trigger any parent updates when comments are saved
              console.log('Comments updated');
            }}
          />
        ) : (
          /* Render sections that belong to the active tab */
          Object.entries(formSections).map(([sectionKey, section]) => (
            section.tab === activeTab && (
              <FormSection key={sectionKey} title={section.title}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderFields(section, sectionKey)}
                </div>
              </FormSection>
            )
          ))
        )}
      </div>
      
      {/* Form Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialData ? 'Update Asset' : 'Save Asset'}
        </button>
      </div>
    </form>
  );
};

export default CrownJewelAssetForm; 